import { deDe } from "@/locales/de-de";
export const enEn: typeof deDe = {
  global: {
    search: "Search",
    previous: "Previous",
    next: "Next",
    name: "Name",
    date: "Date",
    layout: "Layout",
    quantity: "Quantity",
    entriesPerPage: "entires per page",
    showFromToTotal: "Showing {start} to {end} of {total} entries",
    requestError: "Request for '{url}' failed.",
    responseError: "Processing response of '{url}' failed.",
    requestAuthorizationError: "Invalid authentication of request to '{url}'. Please sign in again.",
    responseParseError:
      "Processing response of '{url}' failed. The response structure doesn't match the expected structure.",
    searchTimestamp: "Search from start date",
    description: "Description",
    noDescription: "No description",
    language: "Language",
    theme: "Color theme",
    settings: "Settings",
    userInputs: "User Inputs",
    documentation: "Documentation",
    error: "An error has occurred",
    dateFormat: "MM.DD.YYYY",
    loadingSkipHint: "(You can hide the Loader by left clicking)",
    skipEntryAnimation: "Hide loading animation",
    dates: {
      today: "Today",
      yesterday: "Yesterday",
      weekdays: {
        1: "Monday",
        2: "Tuesday",
        3: "Wednesday",
        4: "Thursday",
        5: "Friday",
        6: "Saturday",
        0: "Sunday",
      },
      lastDay: "Last {day}",
      lastWeek: "Last Week {day}",
      thisMonth: "This Month",
      lastMonth: "Last Month",
      previousMonth: "Previous Month",
      longerTimer: "A long time ago",
    },
    close: "Close"
  },
  app: {
    name: "Rittal External Supplier Print",
    supplier: {
      info: {
        name: "Supplier",
        list: "Supplier list",
        select: "Select a supplier",
        sort: "Sort suppliers by name or by ID",
      },
      csvData: {
        tooltip: "Download CSV data of the selected supplier {supplier}",
        loadingData: {
          success: "Successfully downloaded CSV data for supplier {supplier}",
          failed: "Failed to download CSV data for supplier {supplier}",
        },
      },
      loadingData: {
        info: "Loading Suppliers",
        success: "Successfully loaded Suppliers",
        error: "Failed to get suppliers. {message}",
        empty:
          "No suppliers were found for the user. (Request was successful but the supplier list in the response was empty)",
      },
    },
    user: {
      info: {
        name: "User",
      },
      tooltip: {
        logout: "Log out",
      },
      loadingData: {
        info: "Authenticate User",
        success: "User authenticated",
        error:
          "An error occurred during authentication. Please reload the site.",
        renewError:
          "User could not be authenticated in the background. Try reloading the application. {message}",
      },
    },
    system: {
      info: {
        name: "System",
        list: "System list",
      },
      tooltip: { select: "Select the System to print" },
      loadingData: {
        info: "Loading Systems",
        success: "Successfully loaded Systems",
        error: "Failed to get Systems. {message}",
        empty:
          "No Systems were found for user. (Request was successful but the system list in the response was)",
      },
    },
    material: {
      info: {
        name: "Article number",
        check: "Check Material",
        removeCharacters: "Special Characters were removed from material input",
      },
      tooltip: {
        input: "Input the article number",
      },
      loadingData: {
        info: "Checking article",
        success: "Article Number is valid",
        error: "Failed to validate Material. {message}",
        invalid: "{material} is not a valid article number",
        invalidShort: "Invalid article number",
      },
    },
    printLabels: {
      info: {
        name: "Label list",
        label: "Label",
        labelPreview: "Label preview",
        preview: "Preview",
        print: "Print labels",
        pieces: "Quantity",
        size: "Size",
        downloadSelected: "Download selected",
        notReleasedInfo: "Not released (The property 'Released' is not set)",
      },
      tooltip: {
        preview: "View label preview image",
        quantity: "Set the quantity to print",
        csv: "Download print data as CSV File",
      },
      loadingData: {
        info: "Loading Label List",
        success: "Labels loaded",
        error: "Failed to load labels. {message}",
        empty: "No labels found",
      },
      loadingPreview: {
        info: "Loading Label Image",
        error: "Could not load preview image. {message}",
        retry: "Retry",
      },
      loadingDownload: {
        info: "Downloading label",
        success: "Successfully downloaded label {label}.",
        errorShort:
          "Generation time for {label} ({quantity} pieces) exceeded. PDF will be available in 'PDF History'",
        error:
          "The generating time of {label} for {quantity} pieces has been exceeded. The PDF will be provided in the 'PDF History' tab as soon as the generation is completed",
        errorHistoryLink: "To PDF-History",
      },
      loadingCsv: {
        error: "Failed to download CSV. {message}",
      },
    },
    cancelSerials: {
      info: {
        name: "Serial number",
        nameFull: "Cancel serial numbers",
        list: "Serial list",
        cancel: "Cancel",
        selectAll: "Select all",
        searchTimestamp: "Search in period of time",
        search: "Search serial number",
        timeFormat: "DD.MM.YYYY",
        type: "Type",
      },
      loadingData: {
        info: "Searching Serials",
        success: "Serials loaded",
        error: "Failed to load Serials. {message}",
        empty: "No Serials were found",
        limit:
          "More than 4000 messages were found. You will only see the first 4000 hits. Please specify your search query.",
      },
      loadingCancel: {
        info: "Canceling Serials",
        success: "Serials canceled successfully",
        arrived: "The cancellation request was successfully transferred.",
        error: "Failed to cancel Serials. {msg}",
      },
    },
    pdfHistory: {
      info: {
        name: "PDF history",
        generationLoadingTitle: "Label is still generating",
        generationLoadingText: "Refresh the search to update the status",
      },
      tooltip: {
        download: "Download label PDF",
        newPdf: "This is a new PDF",
      },
      loadingData: {
        info: "Loading PDF History",
        success: "PDF History loaded",
        error: "Could not load PDF History. {message}",
        empty: "No PDFs were found",
      },
      loadingDownload: {
        error:
          "Could not download PDF (The PDF is probably still generating). Please try again later. {message}",
        success: "Successfully downloaded PDF {pdf}.",
      },
    },
    documentation: {
      tooltip: "Open Help",
      title: "App Documentation",
      keyPoints: "Key points",
      print: {
        title: "Print new labels",
        text: `You can generate new labels in the "Print Labels"-tab. Find the label you want to be generated and input the number of copies or serials you want to be generating (Note that the amount of labels generated at once is limited to 10.000). 
        When you have made you selection press the download-button on the right.

        Once the download is finished you can access the newly generated pdf file.

        Sometimes it is possible that the generation time exceeds the request timeout. If that happens you will get a notification. When the generation is done you can download the generated pdf in the "PDF-History"-tab.
        
        You can also preview a label before you generate it. To do that you can click the preview-button on the left of each row`,
        keyPoints:
          'Go to the "Generate Labels"-tab,Specify the amount of labels you want to be generated,Press the download button',
      },
      cancel: {
        title: "Cancel generated Serials",
        text: `You can cancel generated serials in the "Cancel Serials"-tab. You can narrow down the amount of serials by specifying a start date or search for a specific serial or pattern directly.
        
        Once you have set your filter you can press the search button to list the serials that match you filter.
        Now you can select the serials you want to be canceled. When you have made your selection press the cancel-button on the bottom of the page.
        
        Once the cancel process is finished you see a notification and the serial list refreshes itself.

        Sometimes it is possible that the generation time exceeds the request timeout. If that happens you will get a notification. 
        When the generation is done you can download the generated pdf in the "PDF History"-tab.`,
        keyPoints:
          'Go to the "Cancel Serials"-tab,(optional) Set a filter to narrow down the displayed serials,Select the serials you want to be canceled,Press the cancel button',
      },
      download: {
        title: "Download already generated labels",
        text: `You can download generated labels in the "PDF History"-tab. You can narrow down the amount of pdf's by specifying a start date.
        
        Once you have set you filter you can press the search button to list the pdf's that match your filter.
        Now you can download the label you want by pressing the download button on the right of each row.
        
        Once the download is finished you see a notification.
        
        Sometimes it is possible that the label is still generating and the pdf isn't available yet but already shows as a row. 
        If that happens you will get a notification. 
        When the generation is done you can download the generated pdf successfully.`,
        keyPoints: `Go to the "PDF History"-tab,(optional) Set a date filter to narrow down the displayed pdf's,Press the download button for the label you want`,
      },
      logout: {
        title: "Log out",
        text: `You can find the logout button in the side menu. You can open it on the top right corner.
        
        When the menu is open there is the logout button at the bottom. When pressed, you will be prompted to type in you credentials.`,
        keyPoints:
          "Open the Menu on the top right,Click on the Logout-Button at the bottom",
      },
      theme: {
        title: "Change app color theme",
        text: `You can find the color settings in the side menu. You can open it on the top right corner.
        
        When the menu is open there is the color theme switch. When pressed, the application changes the color theme.`,
        keyPoints:
          "Open the Menu on the top right,Toggle the color theme as you like",
      },
    },
  },
};
